import React from 'react'
import styles from './OrderForm.module.css'
import Input from '../Input'
import Banner from '../Banner'
import { OrderUpdateError } from '../../types/Order'

interface Props {
  customerName: string
  customerEmail: string
  customerPhone: string
  error?: OrderUpdateError
  onInputChange: (inputField: string, value: string) => void
  onEmailValidation?: (isValid: boolean) => void
  onNameValidation?: (isValid: boolean) => void
  onPhoneValidation?: (isValid: boolean) => void
  validNameProp?: boolean
  validPhoneProp?: boolean
  validEmailProp?: boolean
  noBottomSpacing?: boolean
  headingText?: string
  headingSize?: string
}

function CustomerDetails ({
  customerName,
  customerEmail,
  customerPhone,
  error,
  onInputChange,
  onEmailValidation,
  onNameValidation,
  onPhoneValidation,
  validNameProp,
  validPhoneProp,
  validEmailProp,
  noBottomSpacing = false,
  headingText = 'Ändra kunduppgifter',
  headingSize = '1.25rem'
}: Props) {
  const [validName, setValidName] = React.useState<boolean>(true)
  const [validPhone, setValidPhone] = React.useState<boolean>(true)
  const [validEmail, setValidEmail] = React.useState<boolean>(true)

  const isValidName = (value: string) => {
    const isValid = value.length > 0
    setValidName(isValid)
    onNameValidation && onNameValidation(isValid)
  }

  const isValidPhone = (value: string) => {
    const isValid = value.length >= 10 && /^[0-9\s+-]*$/.test(value)
    setValidPhone(isValid)
    onPhoneValidation && onPhoneValidation(isValid)
  }

  const isValidEmail = (value: string) => {
    const regExp = /\S+@\S+\.\S+/
    const isValid = regExp.test(value)
    setValidEmail(isValid)
    onEmailValidation && onEmailValidation(isValid)
  }

  const handleChange = (fieldToUpdate: string, e: React.ChangeEvent<HTMLInputElement>) => {
    onInputChange(fieldToUpdate, e.target.value)
  }

  const displayValidName = typeof validNameProp === 'boolean' ? validNameProp : validName
  const displayValidPhone = typeof validPhoneProp === 'boolean' ? validPhoneProp : validPhone
  const displayValidEmail = typeof validEmailProp === 'boolean' ? validEmailProp : validEmail

  return (
    <>
      {error && error.isError && (
        <div className={styles.bannerContainer}>
          <Banner
            theme='warning'>
            <span>{error.errorText}</span>
          </Banner>
        </div>
      )}
      <div className={`${styles.customerSection} ${noBottomSpacing ? styles.noBottomSpacing : ''}`}>
        <h4 className={styles.headingWithPadding} style={{ fontSize: headingSize }}>{headingText}</h4>
        <div className={styles.inputContainer}>
          <Input 
            value={customerName} 
            onBlur={() => isValidName(customerName)} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('name', e)} 
            error={!displayValidName} 
            errorText='Ange ett namn' 
            type='text' 
            id='namn' 
            placeholder='Namn'
            autoComplete='name' 
            required={true} />
          <Input 
            value={customerPhone} 
            onBlur={() => isValidPhone(customerPhone)} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('phone', e)} 
            error={!displayValidPhone} 
            errorText='Ange ett giltigt telefonnummer' 
            type='tel' 
            id='telefonnummer' 
            placeholder='Telefonnummer' 
            autoComplete='tel' 
            inputMode='numeric' 
            required={true} />
          <Input 
            value={customerEmail} 
            onBlur={() => isValidEmail(customerEmail)} 
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e)} 
            error={!displayValidEmail} 
            errorText='Ange en giltig mejladress (t.e.x namn@exempel.se)' 
            type='text' 
            id='mejladress' 
            placeholder='Mejladress' 
            autoComplete='email' 
            required={true} />
        </div>
      </div>
    </>
  )
}

export default CustomerDetails
